// src/pages/PhotosPage.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Photo as PhotoIcon,
  Lock as LockIcon,
  Share as ShareIcon,
  PhotoLibrary as FamilyPhotoIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { PersonalDocuments, Vault, SharedDocuments, FamilyDocumentsTab, FamilyVaultTab } from '../Components/Documents/index';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { useSearchParams } from 'react-router-dom';
import { isFamilyAdmin } from '../services/familyDocumentService';

/**
 * PhotosPage
 *
 * Photo management system with two-tier family architecture
 *
 * For Regular Members (4 tabs):
 * 1. Personal Photos - User's private photos
 * 2. Family Photos - Admin-curated photos (view only)
 * 3. Photo Vault - Personal secure photos with sharing
 * 4. Shared With Me - Photos shared by others
 *
 * For Family Admins (5 tabs):
 * 1. Personal Photos - User's private photos
 * 2. Family Photos - Admin-curated photos (can upload/manage)
 * 3. Photo Vault - Personal secure photos with sharing
 * 4. Family Photo Vault - Admin-only vault with granular access control
 * 5. Shared With Me - Photos shared by others
 */
const PhotosPage = () => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const [searchParams] = useSearchParams();
  const [currentTab, setCurrentTab] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Check if user is family admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (currentUser && profile?.familyCode) {
        const adminStatus = await isFamilyAdmin(profile.familyCode, currentUser.uid);
        setIsAdmin(adminStatus);
      }
    };
    checkAdminStatus();
  }, [currentUser, profile]);

  // Map tab query parameter to tab index and update when isAdmin changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const tabMap = {
        'personal': 0,
        'family': 1,
        'vault': 2,
        'familyVault': 3,
        'shared': isAdmin ? 4 : 3
      };
      const tabIndex = tabMap[tabParam];
      if (tabIndex !== undefined) {
        setCurrentTab(tabIndex);
      }
    }
  }, [searchParams, isAdmin]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  if (!currentUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Please sign in to access photos.
        </Alert>
      </Box>
    );
  }

  if (!profile?.familyCode) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Please join a family to access photo management.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <PhotoIcon color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h4" gutterBottom>
            Photos
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderRadius: 2 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: { xs: 48, sm: 64 },
              textTransform: 'none',
              fontSize: { xs: '0.8rem', sm: '1rem' },
              minWidth: { xs: 100, sm: 'auto' },
            }
          }}
        >
          <Tab icon={<PhotoIcon />} iconPosition="start"
            label={isMobile ? 'Personal' : <Box><Typography variant="subtitle1">Personal Photos</Typography><Typography variant="caption" color="text.secondary">Your private photos</Typography></Box>}
          />
          <Tab icon={<FamilyPhotoIcon />} iconPosition="start"
            label={isMobile ? 'Family' : <Box><Typography variant="subtitle1">Family Photos</Typography><Typography variant="caption" color="text.secondary">{isAdmin ? 'Admin-curated photos' : 'Photos for all'}</Typography></Box>}
          />
          <Tab icon={<LockIcon />} iconPosition="start"
            label={isMobile ? 'Vault' : <Box><Typography variant="subtitle1">Photo Vault</Typography><Typography variant="caption" color="text.secondary">Secure & shareable</Typography></Box>}
          />
          {isAdmin && (
            <Tab icon={<SecurityIcon />} iconPosition="start"
              label={isMobile ? 'Fam. Vault' : <Box><Typography variant="subtitle1">Family Photo Vault</Typography><Typography variant="caption" color="text.secondary">Admin-only access control</Typography></Box>}
            />
          )}
          <Tab icon={<ShareIcon />} iconPosition="start"
            label={isMobile ? 'Shared' : <Box><Typography variant="subtitle1">Shared With Me</Typography><Typography variant="caption" color="text.secondary">Photos from family</Typography></Box>}
          />
        </Tabs>

        {/* Tab panels */}
        <Box sx={{ minHeight: '60vh' }}>
          {currentTab === 0 && (
            <PersonalDocuments
              collectionName="personalPhotos"
              fileTypeFilter="image/*"
              defaultView="gallery"
              title="Personal Photos"
              uploadButtonText="Upload Photos"
              emptyMessage="No photos yet. Upload your first photo to get started!"
            />
          )}
          {currentTab === 1 && (
            <FamilyDocumentsTab
              storageType="photos"
              fileTypeFilter="image/*"
              acceptFileTypes="image/*"
              title="Family Photos"
              uploadButtonText="Upload Photos"
              dialogTitle="Upload Family Photos"
              categoryOptions={[
                { value: 'general', label: 'General' },
                { value: 'family-events', label: 'Family Events' },
                { value: 'vacations', label: 'Vacations' },
                { value: 'holidays', label: 'Holidays' },
                { value: 'celebrations', label: 'Celebrations' },
                { value: 'portraits', label: 'Portraits' },
                { value: 'reunions', label: 'Reunions' },
                { value: 'milestones', label: 'Milestones' }
              ]}
            />
          )}
          {currentTab === 2 && (
            <Vault
              fileTypeFilter="image/*"
              defaultView="gallery"
              title="Photo Vault"
              uploadButtonText="Upload Photos"
              emptyMessage="No photos in vault yet. Upload photos to share with family members!"
            />
          )}
          {isAdmin && currentTab === 3 && (
            <FamilyVaultTab
              storageType="photos"
              title="Family Photo Vault"
              dialogTitle="Upload to Family Photo Vault"
              acceptFileTypes="image/*"
              categoryOptions={[
                { value: 'private', label: 'Private Family' },
                { value: 'sensitive', label: 'Sensitive' },
                { value: 'historical', label: 'Historical' },
                { value: 'medical', label: 'Medical Records' },
                { value: 'legal', label: 'Legal Documents' },
                { value: 'other', label: 'Other' }
              ]}
            />
          )}
          {currentTab === (isAdmin ? 4 : 3) && (
            <SharedDocuments
              fileTypeFilter="image/*"
              defaultView="gallery"
              title="Shared Photos"
              emptyMessage="No shared photos yet. Family members haven't shared any photos with you."
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default PhotosPage;
